import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin } from "lucide-react";

interface RouteMapProps {
  from: string;
  to: string;
}

const cleanLocationName = (name: string) =>
  name.replace(/^[✈️🏨🏠🏖️🌴]+\s*/u, '').trim();

const RouteMap = ({ from, to }: RouteMapProps) => {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchEmbedUrl = async () => {
      try {
        setLoading(true);
        setError(false);
        const { data, error: fnError } = await supabase.functions.invoke("google-maps", {
          body: { action: "embed-directions", origins: cleanLocationName(from), destinations: cleanLocationName(to) },
        });
        if (fnError || !data?.url) {
          setError(true);
          return;
        }
        setEmbedUrl(data.url);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    if (from && to) fetchEmbedUrl();
  }, [from, to]);

  if (error) {
    return (
      <div className="w-full h-48 rounded-xl bg-muted flex items-center justify-center gap-2 text-muted-foreground text-sm">
        <MapPin className="w-4 h-4" />
        Map unavailable
      </div>
    );
  }

  if (loading || !embedUrl) {
    return <Skeleton className="w-full h-48 rounded-xl" />;
  }

  return (
    <div className="w-full h-48 rounded-xl overflow-hidden border border-border">
      <iframe
        src={embedUrl}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="Route map"
      />
    </div>
  );
};

export default RouteMap;
